import { merge } from 'es-toolkit/compat';

import { IMAGE_PROVIDER_URLS } from '../../../constants';
import type { ImageModifiers } from './utils';
import { buildProviderUrl } from './utils';

const BASE_URL = IMAGE_PROVIDER_URLS.CLOUDINARY;

const convertHextoRGBFormat = (value: string) => (value.startsWith('#') ? value.replace('#', 'rgb_') : value);

/**
 * Parameters (option and value pairs) that can be used in the <transformations> segment of the Cloudinary transformation URL.
 * Options and their respective values are connected by an underscore (eg `w_250` for width of 250px.).
 * Multiple parameters are comma separated (eg. `w_250,h_250`).
 *
 * `keyMap` maps the option to its option prefix.
 * `valueMap` is used for grouping options using the same `keyMap` value under a 'category', allowing for easier usage and custom labelling in the component context.
 *
 * Transformation URL structure:
 * https://cloudinary.com/documentation/transformation_reference
 *
 * Transformation URL parameters (options and values):
 * https://cloudinary.com/documentation/image_transformations#transformation_url_syntax
 *
 */
export const operationsGenerator = buildProviderUrl({
  keyMap: {
    fit: 'c',
    width: 'w',
    height: 'h',
    format: 'f',
    quality: 'q',
    background: 'b',
    dpr: 'dpr',
  },
  valueMap: {
    fit: {
      fill: 'fill',
      inside: 'pad',
      outside: 'lpad',
      cover: 'fit',
      contain: 'scale',
    },
    format: {
      jpeg: 'jpg',
    },
    background(value: string) {
      return convertHextoRGBFormat(value);
    },
  },
  joinWith: ',',
  formatter: (key, value) => `${key}_${value}`,
});

// Note: Not configurable via Image props (for now).
const defaultModifiers = {
  format: 'auto',
  quality: 'auto:best',
};

export function getImageUrl(src: string, modifiers: Partial<ImageModifiers> = {}): string {
  const mergeModifiers = merge(defaultModifiers, modifiers);
  const operations = operationsGenerator(mergeModifiers);

  return `${BASE_URL}/${operations}/${src}`;
}
